import { useState, useMemo } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { ChevronRight } from 'lucide-react'
import { useTea } from '@/entities/product/api/use-products'
import { useTeaCategories } from '@/entities/product/api/use-categories'
import { TeaCard } from '@/entities/product/ui/tea-card'
import { CatalogFilters } from '@/widgets/catalog-filters'
import { CatalogSort } from '@/widgets/catalog-sort'
import { useTelegram } from '@/shared/lib/telegram'

/**
 * Виджет: Каталог чая для главной страницы
 * 
 * Компактная версия каталога в стиле главной страницы.
 * Категории загружаются с API.
 */
export function HomeCatalog() {
  const navigate = useNavigate()
  const { hapticFeedback } = useTelegram()
  
  const [category, setCategory] = useState<string | undefined>(undefined)
  const [sort, setSort] = useState<'popular' | 'price-asc' | 'price-desc' | 'newest'>('popular')
  
  // Загружаем категории с API
  const { data: categoriesData } = useTeaCategories()
  const rawCategories = categoriesData?.data || []
  
  // Форматируем категории для фильтров
  const categories = useMemo(() => 
    rawCategories.map(cat => ({
      value: cat.id,
      label: cat.name,
    })),
    [rawCategories]
  )
  
  // Загружаем чай
  const { data, isLoading } = useTea({
    category,
    sort,
    limit: 20,
  })
  
  const teas = data?.data || []
  const totalTeas = data?.meta?.pagination?.total || teas.length
  
  const handleViewAll = () => {
    hapticFeedback.impactOccurred('light')
    navigate({
      to: '/catalog/$section',
      params: { section: 'tea' },
      search: {
        ...(category && { category }),
        ...(sort !== 'popular' && { sort }),
      },
    })
  }
  
  return (
    <div className="mb-6">
      {/* Заголовок секции */}
      <div className="px-4 mb-4">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h2 className="text-xl font-bold text-ink dark:text-white">Каталог чая</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Найдите сорт, который хотите попробовать или сохранить в дневнике.
            </p>
          </div>
          <button
            onClick={() => {
              hapticFeedback.impactOccurred('light')
              navigate({ 
                to: '/catalog/$section',
                params: { section: 'tea' },
              })
            }}
            className="text-sm text-primary/80 dark:text-primary-light font-medium hover:text-primary dark:hover:text-primary-light transition-colors flex items-center gap-1 touch-target"
          >
            Все
            <ChevronRight className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>
        
        {/* Фильтры и сортировка */}
        <div className="flex items-center gap-2 flex-wrap">
          <CatalogFilters
            currentCategory={category}
            categories={categories}
            onFiltersChange={(filters) => setCategory(filters.category)}
            asSheet={true}
          />
          <CatalogSort
            currentSort={sort}
            onSortChange={(newSort) => setSort(newSort)}
            asSheet={true}
          />
        </div>
      </div>
      
      {/* Список чая */}
      {isLoading ? (
        <div className="px-4">
          <div className="grid grid-cols-2 gap-4">
            {Array.from({ length: 4 }).map((_, index) => (
              <div
                key={index}
                className="bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 dark:from-[#1F2123] dark:via-[#1A1C1E] dark:to-[#1F2123] rounded-[16px] h-64 animate-pulse shadow-sm dark:shadow-md"
              />
            ))}
          </div>
        </div>
      ) : teas.length === 0 ? (
        <div className="px-4 py-8 text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Чай не найден. Попробуйте изменить фильтры.
          </p>
        </div>
      ) : (
        <>
          <div className="px-4">
            <div className="grid grid-cols-2 gap-4">
              {teas.slice(0, 6).map((tea) => (
                <TeaCard key={tea.id} tea={tea} />
              ))}
            </div>
          </div>
          
          {/* Кнопка "Показать все" */}
          {totalTeas > 6 && (
            <div className="px-4 mt-4">
              <button
                onClick={handleViewAll}
                className="w-full py-3 px-4 rounded-xl bg-white/80 backdrop-blur-sm border border-white/18 text-primary dark:bg-dark-elevated dark:border-dark-border dark:text-primary-light font-medium hover:bg-white/90 hover:border-primary/30 dark:hover:bg-dark-card active:bg-white/95 dark:active:bg-dark-card transition-colors touch-target"
              >
                Показать все ({totalTeas})
              </button>
            </div>
          )}
        </>
      )}
    </div>
  )
}
